import { useState, useEffect } from "react";
import { Outlet, useLocation, Link } from "react-router-dom";
import { List, MagnifyingGlass } from "@phosphor-icons/react";
import Sidebar from "./Sidebar";
import LlmBudgetBanner from "./LlmBudgetBanner";
import { Toaster } from "./ui/sonner";
import { useReminderPolling } from "../hooks/useReminderPolling";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";

const SW_VERSION_KEY = "journey:sw-version";

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

export default function AppLayout() {
  const isDesktop = useIsDesktop();
  const [mobileOpen, setMobileOpen] = useState(false);
  const sidebarOpen = isDesktop ? true : mobileOpen;
  const setSidebarOpen = isDesktop ? () => {} : setMobileOpen;
  const { settings } = useApp();
  const location = useLocation();
  useReminderPolling(true); // always poll — toast even if browser perms not granted

  // Listen for SW activation messages — show subtle toasts on:
  //   (a) first install (no stored version) → "Journey is ready — works offline."
  //   (b) genuine update (stored !== incoming) → "App updated — Refresh to apply…"
  // Same-version messages are silently ignored.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const onMessage = (event) => {
      const msg = event.data;
      if (!msg || msg.type !== "sw-activated" || !msg.version) return;
      let stored = null;
      try {
        stored = localStorage.getItem(SW_VERSION_KEY);
      } catch {}
      if (!stored) {
        // First install — quiet PWA-readiness confirmation
        toast("Journey is ready", {
          description: "Works offline.",
          duration: 5000,
          id: "sw-first-install",
        });
      } else if (stored !== msg.version) {
        toast("App updated", {
          description: "Refresh to apply the latest changes.",
          duration: 8000,
          id: "sw-updated",
          action: {
            label: "Refresh",
            onClick: () => window.location.reload(),
          },
        });
      }
      try {
        localStorage.setItem(SW_VERSION_KEY, msg.version);
      } catch {}
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, []);

  const titles = {
    "/app": "Chat",
    "/app/memory": "Memory",
    "/app/reminders": "Reminders",
    "/app/tasks": "Tasks",
    "/app/contacts": "Contacts",
    "/app/search": "Search",
    "/app/settings": "Settings",
  };
  const title = titles[location.pathname] || "Journey";

  return (
    <div className="flex h-screen bg-[#050505] text-white grain overflow-hidden">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main className="flex-1 flex flex-col min-w-0 relative z-10">
        <LlmBudgetBanner />
        <header
          className="lg:hidden glass-strong sticky top-0 z-30 flex items-center gap-3 px-4 h-14 border-b border-white/5 safe-top safe-x"
          data-testid="mobile-header"
        >
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-3 -ml-3 -my-1 text-white/80 hover:text-white"
            aria-label="Open menu"
            data-testid="open-sidebar"
          >
            <List size={20} weight="bold" />
          </button>
          <h1 className="font-display text-base font-medium tracking-tight flex-1">
            {title}
          </h1>
          <Link
            to="/app/search"
            className="p-3 -mr-3 -my-1 text-white/80 hover:text-white"
            aria-label="Search"
            data-testid="mobile-search-button"
          >
            <MagnifyingGlass size={20} weight="bold" />
          </Link>
        </header>
        <div className="flex-1 min-h-0 flex flex-col">
          <Outlet />
        </div>
      </main>
      <Toaster
        position="top-center"
        theme="dark"
        toastOptions={{
          classNames: {
            toast:
              "bg-[#121212] border border-white/10 text-white",
            description: "text-white/70",
          },
        }}
      />
    </div>
  );
}
